import type {
  AllMappingFieldValuesResponse,
  CharacteristicsResponse,
  MappingFieldValue,
  MappingFieldValuesResponse,
  UpdateCharacteristicsRequest,
  UpdateMappingFieldValuesRequest,
} from "@/types/fieldMappingConfig";

const MOCK_CHARACTERISTICS: CharacteristicsResponse = {
  fields: [
    {
      key: "color",
      label: "Color",
      group: "Appearance",
      description: "Primary color of the product.",
      data_type: "string",
      cardinality: "single",
      has_allowed_values: true,
      unit: null,
      position: 0,
      characteristics_count: 3,
      allowed_values: [
        { value: "black", label: "Black", characteristics_count: 2 },
        { value: "white", label: "White", characteristics_count: 1 },
        { value: "blue", label: "Blue", characteristics_count: 0 },
      ],
    },
    {
      key: "key_features",
      label: "Key Features",
      group: "",
      description: "Notable features of the product.",
      data_type: "string_list",
      cardinality: "multi",
      has_allowed_values: false,
      unit: null,
      position: 1,
      characteristics_count: 5,
      allowed_values: [],
    },
    {
      key: "capacity",
      label: "Capacity",
      group: "Dimensions",
      description: "Volume or capacity of the product.",
      data_type: "number",
      cardinality: "single",
      has_allowed_values: false,
      unit: "oz",
      position: 2,
      characteristics_count: 1,
      allowed_values: [],
    },
    {
      // Numeric field matching the `case_pack: [2]` value on the product
      // fixture, so mock reads stay internally consistent across types.
      key: "case_pack",
      label: "Case Pack",
      group: "Dimensions",
      description: "Number of units per case.",
      data_type: "number",
      cardinality: "single",
      has_allowed_values: false,
      unit: null,
      position: 3,
      characteristics_count: 1,
      allowed_values: [],
    },
  ],
};

export function resolveMockCharacteristics(): CharacteristicsResponse {
  return JSON.parse(
    JSON.stringify(MOCK_CHARACTERISTICS),
  ) as CharacteristicsResponse;
}

const bareName = (fieldName: string): string =>
  fieldName.includes(":")
    ? (fieldName.split(":", 2)[1] ?? fieldName)
    : fieldName;

/**
 * Values endpoints serve `characteristics_count: null`; this mirrors a
 * characteristic's configured vocabulary with the count stripped.
 */
export function resolveMockMappingFieldValues(
  fieldName: string,
): MappingFieldValuesResponse {
  const bare = bareName(fieldName);
  const match = MOCK_CHARACTERISTICS.fields.find((c) => c.key === bare);
  return {
    field_name: bare,
    values: (match?.allowed_values ?? []).map((v) => ({
      value: v.value,
      label: v.label,
    })),
  };
}

/** Values across all fields that have a configured vocabulary. */
export function resolveMockAllMappingFieldValues(): AllMappingFieldValuesResponse {
  return {
    fields: MOCK_CHARACTERISTICS.fields
      .filter((c) => c.allowed_values.length > 0)
      .map((c) => resolveMockMappingFieldValues(c.key)),
  };
}

/** Maps write inputs to stored values (mock derives `characteristics_count: 0`). */
const toStoredValues = (
  values: { value: string; label?: string }[],
): MappingFieldValue[] =>
  values.map((v) => ({
    value: v.value,
    label: v.label ?? "",
    characteristics_count: 0,
  }));

/**
 * Applies a characteristics PATCH to the fixture and returns the mutated list,
 * keeping mock/dev mode offline for writes. Entries are keyed by `key`: unknown
 * keys are created, `remove` drops a field, and `allowed_values` replaces the
 * vocabulary (absent = untouched).
 */
export function resolveMockUpdateCharacteristics(
  request: UpdateCharacteristicsRequest,
): CharacteristicsResponse {
  const base = resolveMockCharacteristics();
  const byKey = new Map(base.fields.map((f) => [f.key, f]));

  for (const entry of request.fields) {
    if (entry.remove) {
      byKey.delete(entry.key);
      continue;
    }
    const existing = byKey.get(entry.key);
    const next = existing ?? {
      key: entry.key,
      label: "",
      group: "",
      description: "",
      data_type: "string" as const,
      cardinality: "single" as const,
      has_allowed_values: false,
      unit: null,
      position: byKey.size,
      characteristics_count: 0,
      allowed_values: [],
    };
    byKey.set(entry.key, {
      ...next,
      ...(entry.label !== undefined && { label: entry.label }),
      ...(entry.group !== undefined && { group: entry.group }),
      ...(entry.description !== undefined && {
        description: entry.description,
      }),
      ...(entry.data_type !== undefined && { data_type: entry.data_type }),
      ...(entry.has_allowed_values !== undefined && {
        has_allowed_values: entry.has_allowed_values,
      }),
      ...(entry.unit !== undefined && { unit: entry.unit }),
      ...(entry.position !== undefined && { position: entry.position }),
      ...(entry.allowed_values !== undefined && {
        allowed_values: toStoredValues(entry.allowed_values),
      }),
    });
  }

  return {
    fields: [...byKey.values()].sort((a, b) => a.position - b.position),
  };
}

/**
 * Applies a mapping-field values PATCH (full vocabulary replacement) and returns
 * the resulting values, keeping mock/dev mode offline for writes.
 */
export function resolveMockUpdateMappingFieldValues(
  fieldName: string,
  request: UpdateMappingFieldValuesRequest,
): MappingFieldValuesResponse {
  return {
    field_name: bareName(fieldName),
    values: request.values.map((v) => ({
      value: v.value,
      label: v.label ?? "",
    })),
  };
}
